import { useMemo } from "react";
import { ResponsivePie } from "@nivo/pie";
import { Decimal } from "decimal.js";
import { formatNumberToDisplay, safeParseDecimal } from "@/lib/api/common";
import { getRiskHexColor } from "@/section/risk/RiskManagementFullDashboard/colorPalette";
import { get_commodity_color, get_commodity_border_color } from "@/section/risk/RiskManagementFullDashboard/common";
import type { TaggedRiskRow, TaggedProfile } from "@/lib/api/riskV3";
import type { Commodities } from "@/lib/api/commodity";

// ============================================================================
// Types
// ============================================================================

export interface PieSegment {
  id: string;
  label: string;
  group: string;
  value: number;
  color: string;
  percent: number;
}

export interface V3CustomerCommodityProfileItem {
  customer_id: string;
  customer_name: string;
  commodity_group: string;
  commodity_display_name: string;

  // Production (from profile)
  acreage: number;
  projected_production: number;
  actual_production: number;
  production: number; // effective = actual || projected
  projected_yield: number;
  actual_yield: number;
  total_yield: number;

  // Percent hedged
  percent_hedged: number;

  // Futures breakdown quantities
  hta_quantity: number;
  otc_futures_quantity: number;
  brokerage_quantity: number;
  cash_quantity: number;
  dpq_futures_quantity: number;
  direct_sale_futures_quantity: number;
  open_offer_quantity: number;
  double_up_quantity: number;

  // Physical breakdown quantities
  basis_quantity: number;
  otc_basis_quantity: number;
  cash_physical_quantity: number;
  dpq_basis_quantity: number;
  dpq_unset_quantity: number;
  direct_sale_physical_quantity: number;

  // Pie chart data
  futuresBreakdown: PieSegment[];
  physicalBreakdown: PieSegment[];

  // Totals
  total_production_sold: number;
  total_production_unsold: number;
  unprotected_futures: number;
  unprotected_physical: number;
}

// ============================================================================
// Aggregation: V3 rows + profiles → per-customer per-commodity profile items
// ============================================================================

type QuantityBuckets = {
  hta: Decimal;
  otc_futures: Decimal;
  otc_basis: Decimal;
  brokerage: Decimal;
  cash: Decimal;
  dpq_futures: Decimal;
  dpq_basis: Decimal;
  dpq_unset: Decimal;
  direct_sale_futures: Decimal;
  direct_sale_physical: Decimal;
  basis: Decimal;
  open_offer: Decimal;
  double_up: Decimal;
};

function emptyBuckets(): QuantityBuckets {
  return {
    hta: new Decimal(0),
    otc_futures: new Decimal(0),
    otc_basis: new Decimal(0),
    brokerage: new Decimal(0),
    cash: new Decimal(0),
    dpq_futures: new Decimal(0),
    dpq_basis: new Decimal(0),
    dpq_unset: new Decimal(0),
    direct_sale_futures: new Decimal(0),
    direct_sale_physical: new Decimal(0),
    basis: new Decimal(0),
    open_offer: new Decimal(0),
    double_up: new Decimal(0),
  };
}

function makeCompositeKey(customerId: string, commodityGroup: string): string {
  return `${customerId}::${commodityGroup}`;
}

export function aggregateV3ProfileItems(
  rows: TaggedRiskRow[],
  profiles: TaggedProfile[],
  customerNameMap: Record<string, string>,
): V3CustomerCommodityProfileItem[] {
  // Build profile lookup keyed by (customer_id, commodity_group)
  const profileMap = new Map<string, {
    customer_id: string;
    commodity_group: string;
    acreage: Decimal;
    projected_production: Decimal;
    actual_production: Decimal;
    projected_yield: Decimal;
    actual_yield: Decimal;
    display_name: string;
  }>();

  for (const p of profiles) {
    const key = makeCompositeKey(p.customer_id, p.commodity_group);
    const existing = profileMap.get(key);
    if (existing) {
      // Multiple commodity_types within the same group for one customer (e.g. MW + WW both in "Wheat")
      existing.acreage = existing.acreage.plus(safeParseDecimal({})(p.acreage));
      existing.projected_production = existing.projected_production.plus(safeParseDecimal({})(p.projected_total_production));
      existing.actual_production = existing.actual_production.plus(safeParseDecimal({})(p.actual_total_production));
      existing.projected_yield = existing.projected_yield.plus(safeParseDecimal({})(p.projected_yield));
      existing.actual_yield = existing.actual_yield.plus(safeParseDecimal({})(p.actual_yield));
    } else {
      const displayName = rows.find(
        (r) => r.meta.commodity_group === p.commodity_group
      )?.meta.commodity_display_name ?? p.commodity_group;

      profileMap.set(key, {
        customer_id: p.customer_id,
        commodity_group: p.commodity_group,
        acreage: safeParseDecimal({})(p.acreage),
        projected_production: safeParseDecimal({})(p.projected_total_production),
        actual_production: safeParseDecimal({})(p.actual_total_production),
        projected_yield: safeParseDecimal({})(p.projected_yield),
        actual_yield: safeParseDecimal({})(p.actual_yield),
        display_name: displayName,
      });
    }
  }

  // Aggregate row quantities per (customer_id, commodity_group)
  const quantityMap = new Map<string, QuantityBuckets>();

  const getQ = (key: string) => {
    if (!quantityMap.has(key)) {
      quantityMap.set(key, emptyBuckets());
    }
    return quantityMap.get(key)!;
  };

  for (const row of rows) {
    const customerId = row.customer_id;
    if (!customerId) continue;

    const group = row.meta.commodity_group || "Unknown";
    const key = makeCompositeKey(customerId, group);
    const qty = safeParseDecimal({})(row.quantity ?? undefined);
    const q = getQ(key);
    const m = row.meta;

    if (m.is_hta && m.contributes_to_futures_hedging) {
      q.hta = q.hta.plus(qty);
    }
    if (m.is_otc && m.contributes_to_futures_hedging) {
      q.otc_futures = q.otc_futures.plus(qty);
    }
    if (m.is_otc && m.contributes_to_physical_hedging) {
      q.otc_basis = q.otc_basis.plus(qty);
    }
    if (m.is_brokerage_position && m.is_hedgeable && m.contributes_to_futures_hedging) {
      q.brokerage = q.brokerage.plus(qty);
    }
    if (m.is_cash) {
      q.cash = q.cash.plus(qty);
    }
    if (m.is_dpq && m.contributes_to_futures_hedging) {
      q.dpq_futures = q.dpq_futures.plus(qty);
    }
    if (m.is_dpq && m.contributes_to_physical_hedging && m.physical_contribution_type !== 'dpq_unset') {
      q.dpq_basis = q.dpq_basis.plus(qty);
    }
    if (m.is_dpq && !m.contributes_to_futures_hedging && m.physical_contribution_type === 'dpq_unset') {
      q.dpq_unset = q.dpq_unset.plus(qty);
    }
    if (m.is_direct_sale && m.contributes_to_futures_hedging) {
      q.direct_sale_futures = q.direct_sale_futures.plus(qty);
    }
    if (m.is_direct_sale && m.contributes_to_physical_hedging) {
      q.direct_sale_physical = q.direct_sale_physical.plus(qty);
    }
    if (m.contributes_to_physical_hedging && m.physical_contribution_type === "basis" && m.is_basis_only) {
      q.basis = q.basis.plus(qty);
    }
    if (m.is_open_offer && m.is_hedgeable) {
      q.open_offer = q.open_offer.plus(qty);
    }
    if (m.has_double_up) {
      q.double_up = q.double_up.plus(qty);
    }
  }

  // Build items for each (customer, commodity_group) that has a profile
  const items: V3CustomerCommodityProfileItem[] = [];

  for (const [key, profile] of profileMap.entries()) {
    const q = getQ(key);

    const projProd = profile.projected_production;
    const actProd = profile.actual_production;
    const production = actProd.isZero() ? projProd : actProd;

    const projYield = profile.projected_yield;
    const actYield = profile.actual_yield;
    const totalYield = actYield.isZero() ? projYield : actYield;

    const calcPct = (val: Decimal) =>
      production.isZero() ? new Decimal(0) : val.div(production).times(100);

    const futuresTotal = q.hta
      .plus(q.otc_futures)
      .plus(q.brokerage)
      .plus(q.cash)
      .plus(q.dpq_futures)
      .plus(q.direct_sale_futures);
    const physicalTotal = q.basis
      .plus(q.otc_basis)
      .plus(q.cash)
      .plus(q.dpq_basis)
      .plus(q.dpq_unset)
      .plus(q.direct_sale_physical);

    const percentHedged = calcPct(futuresTotal);

    const unprotectedFutures = Decimal.max(production.minus(futuresTotal), 0);
    const unprotectedPhysical = Decimal.max(production.minus(physicalTotal), 0);

    const futuresBreakdown: PieSegment[] = [
      { label: "HTA", id: "hta", group: "percent_hedged", value: q.hta.toNumber(), color: getRiskHexColor("hta"), percent: calcPct(q.hta).toNumber() },
      { label: "OTC", id: "otc", group: "percent_hedged", value: q.otc_futures.toNumber(), color: getRiskHexColor("otc"), percent: calcPct(q.otc_futures).toNumber() },
      { label: "Brokerage", id: "brokerage", group: "percent_hedged", value: q.brokerage.toNumber(), color: getRiskHexColor("brokerage"), percent: calcPct(q.brokerage).toNumber() },
      { label: "DPQ", id: "dpq_futures", group: "dp", value: q.dpq_futures.toNumber(), color: getRiskHexColor("dpq"), percent: calcPct(q.dpq_futures).toNumber() },
      { label: "Cash", id: "cash", group: "percent_hedged", value: q.cash.toNumber(), color: getRiskHexColor("cash"), percent: calcPct(q.cash).toNumber() },
      { label: "Double Up", id: "double_up_id", group: "open_offer", value: q.double_up.toNumber(), color: getRiskHexColor("brokerage"), percent: calcPct(q.double_up).toNumber() },
      { label: "Open Orders", id: "open_offer_id", group: "open_offer", value: q.open_offer.toNumber(), color: getRiskHexColor("offers"), percent: calcPct(q.open_offer).toNumber() },
      { label: "Direct", id: "direct_futures", group: "percent_hedged", value: q.direct_sale_futures.toNumber(), color: getRiskHexColor("dpq"), percent: calcPct(q.direct_sale_futures).toNumber() },
      { label: "Unprotected", id: "unprotected_future", group: "unprotected", value: unprotectedFutures.toNumber(), color: getRiskHexColor("unprotected"), percent: calcPct(unprotectedFutures).toNumber() },
    ];

    const physicalBreakdown: PieSegment[] = [
      { label: "Basis", id: "basis_physical", group: "percent_hedged", value: q.basis.toNumber(), color: getRiskHexColor("basis"), percent: calcPct(q.basis).toNumber() },
      { label: "OTC", id: "otc_physical", group: "percent_hedged", value: q.otc_basis.toNumber(), color: getRiskHexColor("otc"), percent: calcPct(q.otc_basis).toNumber() },
      { label: "DPQ", id: "dpq_physical", group: "dp", value: q.dpq_basis.toNumber(), color: getRiskHexColor("dpq"), percent: calcPct(q.dpq_basis).toNumber() },
      { label: "DPQ Unset", id: "dpq_unset", group: "dp", value: q.dpq_unset.toNumber(), color: getRiskHexColor("dpq"), percent: calcPct(q.dpq_unset).toNumber() },
      { label: "Cash", id: "cash_physical", group: "percent_hedged", value: q.cash.toNumber(), color: getRiskHexColor("cash"), percent: calcPct(q.cash).toNumber() },
      { label: "Direct", id: "direct_physical", group: "percent_hedged", value: q.direct_sale_physical.toNumber(), color: getRiskHexColor("dpq"), percent: calcPct(q.direct_sale_physical).toNumber() },
      { label: "Unsold", id: "unprotected_physical", group: "unprotected", value: unprotectedPhysical.toNumber(), color: getRiskHexColor("unprotected"), percent: calcPct(unprotectedPhysical).toNumber() },
    ];

    items.push({
      customer_id: profile.customer_id,
      customer_name: customerNameMap[profile.customer_id] || profile.customer_id,
      commodity_group: profile.commodity_group,
      commodity_display_name: profile.display_name,
      acreage: profile.acreage.toNumber(),
      projected_production: projProd.toNumber(),
      actual_production: actProd.toNumber(),
      production: production.toNumber(),
      projected_yield: projYield.toNumber(),
      actual_yield: actYield.toNumber(),
      total_yield: totalYield.toNumber(),
      percent_hedged: percentHedged.toNumber(),
      hta_quantity: q.hta.toNumber(),
      otc_futures_quantity: q.otc_futures.toNumber(),
      brokerage_quantity: q.brokerage.toNumber(),
      cash_quantity: q.cash.toNumber(),
      dpq_futures_quantity: q.dpq_futures.toNumber(),
      direct_sale_futures_quantity: q.direct_sale_futures.toNumber(),
      open_offer_quantity: q.open_offer.toNumber(),
      double_up_quantity: q.double_up.toNumber(),
      basis_quantity: q.basis.toNumber(),
      otc_basis_quantity: q.otc_basis.toNumber(),
      cash_physical_quantity: q.cash.toNumber(),
      dpq_basis_quantity: q.dpq_basis.toNumber(),
      dpq_unset_quantity: q.dpq_unset.toNumber(),
      direct_sale_physical_quantity: q.direct_sale_physical.toNumber(),
      futuresBreakdown,
      physicalBreakdown,
      total_production_sold: futuresTotal.toNumber(),
      total_production_unsold: production.minus(futuresTotal).toNumber(),
      unprotected_futures: unprotectedFutures.toNumber(),
      unprotected_physical: unprotectedPhysical.toNumber(),
    });
  }

  // Sort by customer name, then fixed commodity order
  const COMMODITY_ORDER: Record<string, number> = {
    "Canola": 0,
    "Corn": 1,
    "Soybeans": 2,
    "Wheat": 3,
  };
  items.sort((a, b) => {
    const nameCmp = a.customer_name.localeCompare(b.customer_name);
    if (nameCmp !== 0) return nameCmp;
    const aOrder = COMMODITY_ORDER[a.commodity_display_name] ?? 99;
    const bOrder = COMMODITY_ORDER[b.commodity_display_name] ?? 99;
    return aOrder - bOrder;
  });

  return items;
}

// ============================================================================
// Pie Chart (reused from CustomerProfileCards pattern)
// ============================================================================

function UnifiedPieChart({ data, emptyLabel, formatNumber }: {
  data: PieSegment[];
  emptyLabel: string;
  formatNumber: (num: number) => string;
}) {
  const hasData = data && data.length > 0 && data.some((item) => item.value > 0);

  const pieData = hasData
    ? data.filter((d) => d.value > 0)
    : [{
        id: "empty",
        label: emptyLabel,
        value: 100,
        percent: 100,
        color: "#9ca3af",
      }];

  return (
    <div className="w-24 h-24">
      <ResponsivePie
        data={pieData}
        margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
        innerRadius={0.3}
        padAngle={2}
        cornerRadius={2}
        activeOuterRadiusOffset={2}
        colors={(d) => d.data.color}
        borderWidth={0}
        enableArcLinkLabels={false}
        enableArcLabels={false}
        isInteractive={true}
        tooltip={({ datum }) => (
          <div className="bg-white p-2 shadow-lg rounded border text-xs">
            <div className="font-semibold">{datum.label}</div>
            <div>
              {hasData
                ? `${formatNumber(datum.value)} (${formatNumberToDisplay(datum.data.percent, { suffix: "%" })})`
                : "No data available"}
            </div>
          </div>
        )}
      />
    </div>
  );
}

// ============================================================================
// Profile Cards Component
// ============================================================================

interface V3ProfileCardsProps {
  rows: TaggedRiskRow[];
  profiles: TaggedProfile[];
  customerNameMap: Record<string, string>;
  filteredCustomerIds: string[];
  filteredCommodityGroups?: string[];
}

export function V3ProfileCards({ rows, profiles, customerNameMap, filteredCustomerIds, filteredCommodityGroups }: V3ProfileCardsProps) {
  const items = useMemo(
    () => aggregateV3ProfileItems(rows, profiles, customerNameMap),
    [rows, profiles, customerNameMap],
  );

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  // Group items by customer, only for customers in the filter, capped at 2
  // When commodity groups are specified, only show matching commodity cards
  const customerGroups = useMemo(() => {
    if (!items.length || filteredCustomerIds.length === 0) return [];

    const allowedSet = new Set(filteredCustomerIds);
    const commoditySet = filteredCommodityGroups && filteredCommodityGroups.length > 0
      ? new Set(filteredCommodityGroups)
      : null;

    const groups = new Map<string, V3CustomerCommodityProfileItem[]>();
    for (const item of items) {
      if (!allowedSet.has(item.customer_id)) continue;
      if (commoditySet && !commoditySet.has(item.commodity_group)) continue;
      if (!groups.has(item.customer_id)) {
        groups.set(item.customer_id, []);
      }
      groups.get(item.customer_id)!.push(item);
    }
    // Preserve the order from filteredCustomerIds, capped at 2
    const ordered: [string, V3CustomerCommodityProfileItem[]][] = [];
    for (const cid of filteredCustomerIds) {
      const group = groups.get(cid);
      if (group) {
        ordered.push([cid, group]);
      }
      if (ordered.length >= 2) break;
    }
    return ordered;
  }, [items, filteredCustomerIds, filteredCommodityGroups]);

  if (filteredCustomerIds.length === 0) {
    return null;
  }

  if (customerGroups.length === 0) {
    return <div className="text-sm text-gray-500">No commodity profiles found for filtered customers.</div>;
  }

  return (
    <div className="space-y-6">
      {customerGroups.map(([customerId, customerItems]) => (
        <div key={customerId}>
          <h3 className="text-base font-semibold mb-2">{customerItems[0].customer_name}</h3>
          <div className="flex flex-col 2xl:grid gap-2 2xl:grid-cols-2 5xl:grid-cols-3">
            {customerItems.map((item) => {
              const futuresPieData = item.futuresBreakdown.filter(
                (s) => !["open_offer_id", "double_up_id"].includes(s.id),
              );
              const basisPieData = item.physicalBreakdown;
              const openOrderData = item.futuresBreakdown.find((s) => s.id === "open_offer_id");
              const doubleUpData = item.futuresBreakdown.find((s) => s.id === "double_up_id");

              return (
                <div
                  key={`v3-profile-${item.customer_id}-${item.commodity_group}`}
                  className={`relative p-4 rounded-lg border-2 ${get_commodity_border_color(item.commodity_group as Commodities)} bg-white shadow-sm`}
                >
                  {/* Commodity Header */}
                  <div className="flex items-center gap-3 mb-3 justify-between">
                    <span className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full ${get_commodity_color(item.commodity_group as Commodities)}`} />
                      <h3 className="text-lg font-bold">{item.commodity_display_name}</h3>
                    </span>
                  </div>

                  <div className="flex flex-col md:grid md:grid-cols-1 gap-4">
                    {/* Production Summary */}
                    <div className="grid gap-4 mb-4 text-sm grid-cols-5">
                      <div className="text-center">
                        <div className="text-gray-500">Percent Hedged</div>
                        <div className="font-semibold text-gray-800">
                          {formatNumberToDisplay(item.percent_hedged, { suffix: "%" })}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-500">Acreage</div>
                        <div className="font-semibold text-gray-800">{formatNumber(item.acreage)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-500">Yield</div>
                        <div className="font-semibold text-gray-800">
                          {formatNumberToDisplay(item.total_yield)}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-500">Projected</div>
                        <div className="font-semibold text-gray-800">{formatNumber(item.projected_production)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-500">Actual</div>
                        <div className="font-semibold text-gray-800">{formatNumber(item.actual_production)}</div>
                      </div>
                    </div>

                    {/* Pie Charts + Legends */}
                    <div className="flex flex-col items-start justify-start gap-4 lg:grid lg:grid-cols-2">
                      {/* Futures */}
                      <div className="flex flex-row items-start gap-4">
                        <div className="flex items-center flex-row">
                          <div className="flex-shrink-0">
                            <UnifiedPieChart
                              data={futuresPieData}
                              emptyLabel="No Futures"
                              formatNumber={formatNumber}
                            />
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="gap-1 text-xs grid grid-cols-2 justify-start">
                            {futuresPieData.map((segment) => (
                              <div key={segment.id} className="flex justify-start items-start gap-2">
                                <div
                                  className="w-3 h-3 rounded"
                                  style={{ backgroundColor: segment.color }}
                                />
                                <div className="flex-1">
                                  <div className="font-medium">
                                    {segment.label}{" "}
                                    <span className="text-xs text-muted-foreground">
                                      ({formatNumberToDisplay(segment.percent, { suffix: "%" })})
                                    </span>
                                  </div>
                                  <div className="text-gray-500">{formatNumber(segment.value)}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Physical */}
                      <div className="flex flex-row justify-start gap-4">
                        <div className="flex justify-start flex-row">
                          <div className="flex-shrink-0">
                            <UnifiedPieChart
                              data={basisPieData}
                              emptyLabel="No Physical"
                              formatNumber={formatNumber}
                            />
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="gap-1 text-xs grid grid-cols-2 justify-start items-start">
                            {basisPieData.map((segment) => (
                              <div key={segment.id} className="flex justify-start items-start gap-2">
                                <div
                                  className="w-3 h-3 rounded"
                                  style={{ backgroundColor: segment.color }}
                                />
                                <div className="flex-1">
                                  <div className="font-medium">
                                    {segment.label}{" "}
                                    <span className="text-xs text-muted-foreground">
                                      ({formatNumberToDisplay(segment.percent, { suffix: "%" })})
                                    </span>
                                  </div>
                                  <div className="text-gray-500">{formatNumber(segment.value)}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer: Production Status */}
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <div className="grid grid-cols-4 gap-4 text-xs">
                      <div>
                        <div className="text-gray-500">Production Sold</div>
                        <div className="font-semibold text-green-700">{formatNumber(item.total_production_sold)}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Production Unsold</div>
                        <div className="font-semibold text-red-700">{formatNumber(item.total_production_unsold)}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Double Up</div>
                        <div className="font-semibold text-red-700">
                          {formatNumberToDisplay(doubleUpData?.value ?? 0)} ({formatNumberToDisplay(doubleUpData?.percent ?? 0)}%)
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Open Orders</div>
                        <div className="font-semibold text-red-700">
                          {formatNumberToDisplay(openOrderData?.value ?? 0)} ({formatNumberToDisplay(openOrderData?.percent ?? 0)}%)
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
